import { Product } from './billing-product';
import { CatalogProductMetadata } from './catalog';
import { Nil, Nullable } from './global';
import { PlanInterval } from './pricing';

export enum SubscriptionStatus {
  ACTIVE = 'active',
  ALL = 'ALL',
  CANCELED = 'CANCELED',
  ENDED = 'ENDED',
  INCOMPLETE = 'INCOMPLETE',
  INCOMPLETE_EXPIRED = 'INCOMPLETE_EXPIRED',
  PAST_DUE = 'past_due',
  PAUSED = 'PAUSED',
  TRIALING = 'TRIALING',
  UNPAID = 'UNPAID',
}

export enum SubscriptionUsageType {
  metered = 'metered',
  licensed = 'licensed',
}

export enum SubscriptionInterval {
  yearly = 'year',
  monthly = 'month',
}

export interface SubscriptionPlanMetadata {
  is_free?: string;
  is_legacy?: string;
  is_public?: Nullable<string>;
  name?: string;
  description?: string;
  most_popular?: string;
  comparison_data?: string;
  concurrency?: Nullable<string>;
  request: string;
  rollover: string;
  seat: Nullable<string>;
  developer_portal_consumer_group?: string;
  platform_consumer_group?: string;
  testing_notifications?: string;
}

export type SubscriptionType = 'Request' | 'Credit';

export interface SubscriptionPlan {
  id: string;
  amount: number;
  amountDecimal: number;
  billingScheme: string;
  currency: string;
  interval: PlanInterval;
  intervalCount: number;
  active: boolean;
  nickname: Nil<string>;
  metadata: SubscriptionPlanMetadata;
  trialPeriodDays: Nil<string>;
  usageType: SubscriptionUsageType;
  status: SubscriptionStatus;
  product: Product;
  trial: boolean;
}

export interface SubscriptionPrice {
  lastResponse: Nil<string>;
  rawJsonObject: Nil<string>;
  active: boolean;
  billingScheme: string;
  created: number;
  currency: string;
  currencyOptions: Nil<string>;
  customUnitAmount: Nil<string>;
  deleted: Nil<string>;
  id: string;
  livemode: boolean;
  lookupKey: Nil<string>;
  metadata: {
    request: number;
  };
  nickname: string;
  object: string;
  product: string;
  recurring: {
    lastResponse: Nil<string>;
    rawJsonObject: Nil<string>;
    aggregateUsage: Nil<string>;
    interval: string;
    intervalCount: number;
    trialPeriodDays: Nil<string>;
    usageType: string;
  };
  taxBehavior: string;
  tiers: Nil<string>;
  tiersMode: Nil<string>;
  transformQuantity: Nil<string>;
  type: string;
  unitAmount: number;
  unitAmountDecimal: number;
  productObject: Nil<string>;
}

export interface SubscriptionUser {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  level: Nullable<string>;
}

export interface Credit {
  total?: number;
}

export enum CancelSubscriptionReason {
  CUSTOMER_SERVICE = 'CUSTOMER_SERVICE',
  LOW_QUALITY = 'LOW_QUALITY',
  MISSING_FEATURES = 'MISSING_FEATURES',
  OTHER = 'OTHER',
  TOO_EXPENSIVE = 'TOO_EXPENSIVE',
  TOO_COMPLEX = 'TOO_COMPLEX',
}

export interface CustomerInfo {
  id?: string;
  name?: string;
  email?: string;
}

export interface CancellationDetail {
  comment?: string;
  feedback?: string;
  reason?: string;
}

export interface Discount {
  id: string;
  name: string;
  amountOff: Nullable<number>;
  percentOff: Nullable<number>;
  currency: string;
  duration: string;
  durationInMonths: Nullable<number>;
}

export interface Current {
  amount: number;
  id?: string;
  plan?: Plan;
  cancelAt: string;
  cancelAtPeriodEnd: boolean;
  canceledAt: string;
  cancellationDetails: CancellationDetail;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  daysUntilDue: string;
  description: string;
  startDate: string;
  endedAt: string;
  status: SubscriptionStatus;
  discounts?: Discount[];
}

export interface Next extends Current {}

export interface Plan {
  id: string;
  active?: boolean;
  amount: number;
  amountDecimal?: number;
  billingScheme?: string;
  currency?: string;
  interval?: PlanInterval;
  intervalCount?: number;
  nickname?: string;
  type?: string;
  trial?: true;
  metadata: SubscriptionPlanMetadata;
  product?: {
    id?: string;
    name?: string;
    metadata?: CatalogProductMetadata;
  };
}

export interface EntitlementMetadata {
  group?: string;
  order?: string;
  parent?: string;
  value?: string;
}

export interface Entitlement {
  key: string;
  name: string;
  metadata: EntitlementMetadata;
  products: string[];
}

interface GroupedEntitlements {
  parent: string;
  entitlements: Entitlement[];
}

export interface GroupedEntitlement {
  group: string;
  entitlements: Entitlement[];
}

export interface ParentGroupedEntitlement {
  parent: string;
  groups: GroupedEntitlement[];
}

export interface Subscription {
  customer?: CustomerInfo;
  current?: Current;
  next?: Next;
  credit?: Credit;
  topup?: Topup;
  entitlements?: Entitlement[];
}

interface Topup {
  id: string;
  active: boolean;
  amount: number;
  amountDecimal: number;
  billingScheme: string;
  currency: string;
  interval: string | null;
  intervalCount: number | null;
  metadata: {
    request: string;
  };
  nickname: string;
  product: {
    id: string;
    name: string;
    type: string;
  };
}
